import * as React from 'react';
import { ApiHatasi, api } from '@/lib/api';

/**
 * Rezervasyon duzeltme / uzatma penceresi.
 *
 * Iki giris noktasi ayni pencereyi acar:
 *   - "Uzat"   -> yalnizca cikis tarihi one cikar, hizli kullanim.
 *   - "Duzelt" -> yanlis girilmis kayit icin butun alanlar.
 *
 * Tarih degisince tutar tesisin kendi tarifesinden yeniden hesaplanip
 * ONERILIR; resepsiyonist kabul eder ya da elle degistirir (pazarlik,
 * indirim). Boylece uzatilan gece ucretsiz kalmaz, ama fiyat da
 * kullanicinin elinden alinmaz.
 *
 * Misafir ADI buradan degistirilemez: KBS bildirimi gonderilmis bir kayitta
 * kimlik verisini sessizce degistirmek yasal bildirimle celisir. O duzeltme
 * KBS ekranindaki resmi "sehven" akisindan yapilir.
 */

export interface DuzeltRez {
  id: string;
  kod: string;
  roomId: string | null;
  girisTarihi: string;
  cikisTarihi: string;
  durum: string;
  kaynak: string;
  yetiskin: number;
  cocuk: number;
  toplamKurus: number;
  paraBirimi: string;
  misafirAd: string | null;
  misafirSoyad: string | null;
  not: string | null;
}

interface Oda {
  id: string;
  no: string;
  roomTypeAd: string | null;
  durum: string;
}

const PANSIYONLAR = [
  ['OB', 'Sadece oda'],
  ['BB', 'Kahvaltı dahil'],
  ['HB', 'Yarım pansiyon'],
  ['FB', 'Tam pansiyon'],
  ['AI', 'Her şey dahil'],
] as const;

const KAYNAKLAR = [
  ['DIREKT', 'Doğrudan'],
  ['TELEFON', 'Telefon'],
  ['WALK_IN', 'Kapıdan geldi'],
  ['WEB', 'Web sitesi'],
  ['ACENTA', 'Acenta'],
  ['KANAL', 'Kanal'],
  ['GRUP', 'Grup'],
] as const;

/** Ad + soyad; ikisi de yoksa rezervasyon kodu. */
function tamAd(rez: { misafirAd: string | null; misafirSoyad: string | null; kod: string }): string {
  const ad = [rez.misafirAd, rez.misafirSoyad].map((p) => p?.trim()).filter(Boolean).join(' ');
  return ad || rez.kod;
}

const gunEkle = (iso: string, n: number) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const geceSay = (a: string, b: string) =>
  Math.max(0, Math.round((new Date(`${b}T00:00:00Z`).getTime() - new Date(`${a}T00:00:00Z`).getTime()) / 86_400_000));

const GIRDI =
  'w-full rounded-xl border-[1.5px] border-cizgi bg-white px-3.5 py-2.5 text-[15px] text-lacivert outline-none transition focus:border-marka-600 focus:shadow-[0_0_0_4px_rgba(31,79,216,.12)]';

export function RezervasyonDuzelt({
  rez,
  odalar,
  mod,
  cakisiyorMu,
  onKapat,
  onKaydedildi,
}: {
  rez: DuzeltRez;
  odalar: Oda[];
  mod: 'uzat' | 'duzelt';
  cakisiyorMu: (roomId: string, giris: string, cikis: string, haricId: string) => boolean;
  onKapat: () => void;
  onKaydedildi: (mesaj: string) => void;
}) {
  const icerideMi = rez.durum === 'CHECKED_IN';
  const kapaliMi = rez.durum === 'CHECKED_OUT' || rez.durum === 'IPTAL' || rez.durum === 'NO_SHOW';

  const [roomId, setRoomId] = React.useState(rez.roomId ?? '');
  const [giris, setGiris] = React.useState(rez.girisTarihi);
  const [cikis, setCikis] = React.useState(mod === 'uzat' ? gunEkle(rez.cikisTarihi, 1) : rez.cikisTarihi);
  const [yetiskin, setYetiskin] = React.useState(rez.yetiskin);
  const [cocuk, setCocuk] = React.useState(rez.cocuk);
  const [pansiyon, setPansiyon] = React.useState<string>('BB');
  const [kaynak, setKaynak] = React.useState<string>(rez.kaynak);
  const [tutar, setTutar] = React.useState<string>((rez.toplamKurus / 100).toString());
  const [not, setNot] = React.useState(rez.not ?? '');
  const [hata, setHata] = React.useState<string | null>(null);
  const [gonderiliyor, setGonderiliyor] = React.useState(false);
  const [oneri, setOneri] = React.useState<{ toplamTutar: number | null; eksikGun: string[] } | null>(null);
  const [tutarElle, setTutarElle] = React.useState(false);

  React.useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && !gonderiliyor && onKapat();
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onKapat, gonderiliyor]);

  const gece = geceSay(giris, cikis);
  const tarihDegisti = giris !== rez.girisTarihi || cikis !== rez.cikisTarihi;

  // Tarih degistikce tarife onerisini tazele.
  React.useEffect(() => {
    if (!tarihDegisti || gece < 1) {
      setOneri(null);
      return;
    }
    let iptal = false;
    api<{ toplamTutar: number | null; eksikGun: string[] }>(
      `/api/rezervasyonlar/${rez.id}/tarife?girisTarihi=${giris}&cikisTarihi=${cikis}`,
    )
      .then((o) => {
        if (iptal) return;
        setOneri(o);
        if (!tutarElle && o.toplamTutar !== null) setTutar(String(o.toplamTutar));
      })
      .catch(() => undefined);
    return () => {
      iptal = true;
    };
  }, [rez.id, giris, cikis, gece, tarihDegisti, tutarElle]);

  const cakisma = roomId && gece > 0 ? cakisiyorMu(roomId, giris, cikis, rez.id) : false;
  const gonderilebilir = gece > 0 && !cakisma && !gonderiliyor;

  async function gonder(e: React.FormEvent) {
    e.preventDefault();
    if (!gonderilebilir) return;
    setGonderiliyor(true);
    setHata(null);
    try {
      // Not her zaman gonderilir; bos birakmak "notu sil" anlamina gelir.
      // Alan mevcut notla dolu acildigi icin uzatma sirasinda not kaybolmaz.
      const govde: Record<string, unknown> = { not: not.trim() || null };
      if (!kapaliMi) {
        govde.roomId = roomId || undefined;
        if (!icerideMi) govde.girisTarihi = giris;
        govde.cikisTarihi = cikis;
        govde.yetiskin = yetiskin;
        govde.cocuk = cocuk;
        govde.pansiyon = pansiyon;
        govde.kaynak = kaynak;
        const t = Number(tutar.replace(',', '.'));
        if (Number.isFinite(t)) govde.toplamTutar = t;
      }
      await api(`/api/rezervasyonlar/${rez.id}`, { method: 'PATCH', body: JSON.stringify(govde) });
      onKaydedildi(
        mod === 'uzat'
          ? `${tamAd(rez)} · ${gece} geceye uzatıldı`
          : `${tamAd(rez)} güncellendi`,
      );
    } catch (err) {
      setHata(err instanceof ApiHatasi ? err.message : 'Değişiklik kaydedilemedi.');
      setGonderiliyor(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-lacivert/60 [animation:fadein_.2s_both]" onClick={onKapat} role="presentation">
      <div className="flex min-h-full items-center justify-center p-4">
        <form
          onSubmit={gonder}
          onClick={(e) => e.stopPropagation()}
          className="flex w-full max-w-[520px] flex-col gap-4 rounded-[20px] bg-white p-6 shadow-[0_40px_80px_-32px_rgba(11,21,48,.55)] [animation:popin_.3s_cubic-bezier(.2,.8,.2,1)_both]"
        >
          <div className="flex items-start justify-between gap-4">
            <div>
              <span className="text-[13px] font-semibold text-gri">{rez.kod}</span>
              <h2 className="text-[20px] font-bold tracking-[-.01em] text-lacivert">
                {mod === 'uzat' ? 'Konaklamayı uzat' : 'Rezervasyonu düzelt'}
              </h2>
              <p className="mt-0.5 text-[13px] text-gri">
                {tamAd(rez)} · şu an {geceSay(rez.girisTarihi, rez.cikisTarihi)} gece
              </p>
            </div>
            <button
              type="button"
              onClick={onKapat}
              aria-label="Kapat"
              className="grid size-8 shrink-0 place-items-center rounded-full bg-zemin text-lg leading-none text-gri hover:bg-cizgi"
            >
              ×
            </button>
          </div>

          {kapaliMi ? (
            <p className="rounded-xl bg-[#fff5dc] px-3.5 py-3 text-[13px] leading-relaxed text-[#b9840f]">
              Bu kayıt kapanmış ({rez.durum === 'IPTAL' ? 'iptal' : rez.durum === 'NO_SHOW' ? 'gelmedi' : 'çıkış yapıldı'}).
              Geçmişi değiştirmek folyoyu, raporları ve KBS bildirimini tutarsız yapacağı için yalnızca not
              ekleyebilirsiniz.
            </p>
          ) : (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="flex flex-col gap-1.5 sm:col-span-2">
                  <span className="text-[12.5px] font-semibold text-gri">Oda</span>
                  <select value={roomId} onChange={(e) => setRoomId(e.target.value)} className={GIRDI}>
                    <option value="">Oda atanmamış</option>
                    {odalar.map((o) => (
                      <option key={o.id} value={o.id} disabled={o.durum === 'ARIZALI' || o.durum === 'BLOKE'}>
                        {o.no} · {o.roomTypeAd ?? 'Tip yok'}
                        {o.durum === 'ARIZALI' ? ' (arızalı)' : o.durum === 'BLOKE' ? ' (bloke)' : ''}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="flex flex-col gap-1.5">
                  <span className="flex items-baseline justify-between gap-2 text-[12.5px] font-semibold text-gri">
                    Giriş
                    {icerideMi && <span className="font-medium text-marka-600">misafir içeride</span>}
                  </span>
                  <input
                    type="date"
                    value={giris}
                    disabled={icerideMi}
                    onChange={(e) => {
                      setGiris(e.target.value);
                      if (e.target.value >= cikis) setCikis(gunEkle(e.target.value, 1));
                    }}
                    className={GIRDI + (icerideMi ? ' cursor-not-allowed bg-zemin text-gri' : '')}
                  />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="flex items-baseline justify-between gap-2 text-[12.5px] font-semibold text-gri">
                    Çıkış
                    <span className="font-medium text-marka-600">{gece > 0 ? `${gece} gece` : 'geçersiz'}</span>
                  </span>
                  <input
                    type="date"
                    value={cikis}
                    min={gunEkle(giris, 1)}
                    autoFocus={mod === 'uzat'}
                    onChange={(e) => setCikis(e.target.value)}
                    className={GIRDI}
                  />
                </label>

                <label className="flex flex-col gap-1.5">
                  <span className="text-[12.5px] font-semibold text-gri">Yetişkin</span>
                  <input type="number" min={1} max={10} value={yetiskin} onChange={(e) => setYetiskin(Math.max(1, Number(e.target.value) || 1))} className={GIRDI} />
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className="text-[12.5px] font-semibold text-gri">Çocuk</span>
                  <input type="number" min={0} max={10} value={cocuk} onChange={(e) => setCocuk(Math.max(0, Number(e.target.value) || 0))} className={GIRDI} />
                </label>

                {mod === 'duzelt' && (
                  <>
                    <label className="flex flex-col gap-1.5">
                      <span className="text-[12.5px] font-semibold text-gri">Pansiyon</span>
                      <select value={pansiyon} onChange={(e) => setPansiyon(e.target.value)} className={GIRDI}>
                        {PANSIYONLAR.map(([d, m]) => (
                          <option key={d} value={d}>{m}</option>
                        ))}
                      </select>
                    </label>
                    <label className="flex flex-col gap-1.5">
                      <span className="text-[12.5px] font-semibold text-gri">Kaynak</span>
                      <select value={kaynak} onChange={(e) => setKaynak(e.target.value)} className={GIRDI}>
                        {KAYNAKLAR.map(([d, m]) => (
                          <option key={d} value={d}>{m}</option>
                        ))}
                      </select>
                    </label>
                  </>
                )}

                <label className="flex flex-col gap-1.5 sm:col-span-2">
                  <span className="flex items-baseline justify-between gap-2 text-[12.5px] font-semibold text-gri">
                    Toplam tutar ({rez.paraBirimi})
                    {oneri?.toplamTutar !== null && oneri !== null && (
                      <span className="font-medium text-marka-600">tarifeden hesaplandı</span>
                    )}
                  </span>
                  <input
                    inputMode="decimal"
                    value={tutar}
                    onChange={(e) => {
                      setTutar(e.target.value);
                      setTutarElle(true);
                    }}
                    className={GIRDI}
                  />
                </label>
              </div>

              {oneri && oneri.eksikGun.length > 0 && (
                <p className="rounded-xl bg-[#fff5dc] px-3.5 py-3 text-[12.5px] leading-relaxed text-[#b9840f]">
                  {oneri.eksikGun.length} güne tarife girilmemiş, önerilen tutar eksik olabilir. Tutarı elle
                  düzeltebilirsiniz.
                </p>
              )}

              {cakisma && (
                <p className="rounded-xl bg-[#fdeceb] px-3.5 py-3 text-[13px] font-medium text-kirmizi">
                  Bu oda seçtiğiniz tarihlerde dolu. Başka oda ya da tarih seçin.
                </p>
              )}
            </>
          )}

          <label className="flex flex-col gap-1.5">
            <span className="flex items-baseline justify-between text-[12.5px] font-semibold text-gri">
              Not <span className="font-medium">boş bırakırsanız silinir</span>
            </span>
            <input value={not} onChange={(e) => setNot(e.target.value)} placeholder="Örn. misafir bir gece uzattı" className={GIRDI} />
          </label>

          <p className="rounded-xl bg-zemin px-3.5 py-3 text-[12.5px] leading-relaxed text-gri">
            Misafirin <strong className="text-lacivert">adı ve kimlik bilgisi</strong> buradan değiştirilemez. KBS
            bildirimi gönderilmiş bir kayıtta kimliği sessizce değiştirmek yasal bildirimle çelişir; düzeltme KBS
            ekranından yapılır.
          </p>

          {hata && <p role="alert" className="rounded-xl bg-[#fdeceb] px-3.5 py-3 text-[13px] font-medium text-kirmizi">{hata}</p>}

          <div className="mt-1 flex gap-3">
            <button
              type="button"
              onClick={onKapat}
              disabled={gonderiliyor}
              className="flex-1 rounded-full border-[1.5px] border-cizgi bg-white py-3 text-[15px] font-semibold text-lacivert transition hover:border-gri"
            >
              Vazgeç
            </button>
            <button
              type="submit"
              disabled={!gonderilebilir}
              className="flex flex-1 items-center justify-center gap-2 rounded-full bg-marka-600 py-3 text-[15px] font-semibold text-white transition hover:bg-marka-700 disabled:opacity-40"
            >
              {gonderiliyor && <span className="size-4 rounded-full border-2 border-white/35 border-t-white [animation:spin_.7s_linear_infinite]" />}
              {gonderiliyor ? 'Kaydediliyor…' : mod === 'uzat' ? 'Uzat' : 'Değişikliği kaydet'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
